// Data + actions for the vendor Performance Scorecard, shared between the Performance tab
// (hero stats, trend chart, history) and the review modal (Q&A form, reassignment) so both
// read a single fetch and stay in sync without duplicate API calls.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "./useApi";

export function useVendorPerformanceScorecard(vendorId, canManage, currentUserId) {
    const apiCall = useApi();

    const [catalog,     setCatalog]     = useState(null);
    const [data,        setData]        = useState(null); // { cadenceMonths, cadenceOverride, scorecards }
    const [users,       setUsers]       = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState(null);
    const [actionError, setActionError] = useState(null);
    const [answers,     setAnswers]     = useState({}); // { questionId: 1-5 }
    const [comments,    setComments]    = useState("");
    const [saving,      setSaving]      = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [cat, list, assignable] = await Promise.all([
                apiCall("GET", "/tprm-performance-scorecards/catalog"),
                apiCall("GET", `/TPRMVendor/${vendorId}/performance-scorecards`),
                apiCall("GET", "/assessments/assignableUsers").catch(() => []),
            ]);
            setCatalog(cat);
            setData(list);
            setUsers(assignable || []);
            setError(null);
        } catch {
            setError("Could not load performance scorecards.");
        } finally {
            setLoading(false);
        }
    }, [apiCall, vendorId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const scorecards = data?.scorecards || [];
    const open = scorecards.find(s => s.status !== "Completed");

    // Reset the draft whenever the vendor or the open scorecard changes — the component instance
    // survives route-param navigation, and stale ratings must never carry over to another vendor.
    useEffect(() => { setAnswers({}); setComments(""); }, [vendorId, open?.id]);
    const completed = scorecards.filter(s => s.status === "Completed" && s.overallScore != null);
    const latest = completed[0]; // history arrives newest-first

    const isReviewer = open && currentUserId && open.reviewerUserId === currentUserId;
    const canFill = !!open && (isReviewer || canManage);

    const themes = useMemo(() => {
        if (!catalog) return [];
        const byTheme = {};
        // Sequence number reflects the catalog's own question order (stable, not re-sorted per
        // theme) so reviewers can reference "Q7" consistently across sessions and exports.
        catalog.questions.forEach((q, i) => { (byTheme[q.theme] = byTheme[q.theme] || []).push({ ...q, seq: i + 1 }); });
        return Object.entries(byTheme).map(([theme, questions]) => ({ theme, questions }));
    }, [catalog]);

    // Response scale from the backend catalog — the single source of truth, so a future
    // DB-configurable catalog can't drift from hardcoded labels here.
    const scale = useMemo(() => catalog
        ? Object.entries(catalog.scaleLabels)
            .map(([value, label]) => ({ value: Number(value), label }))
            .sort((a, b) => a.value - b.value)
        : [], [catalog]);

    const liveThemeAvg = (questions) => {
        const vals = questions.map(q => answers[q.questionId]).filter(Boolean);
        return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : "—";
    };
    const answeredCount = catalog ? catalog.questions.filter(q => answers[q.questionId]).length : 0;
    const allAnswered = catalog && answeredCount === catalog.questions.length;

    const chartData = useMemo(() => {
        // Disambiguate duplicate period labels (two reviews completed in the same quarter)
        const seen = {};
        return [...completed].reverse().map(s => {
            const count = (seen[s.reviewPeriod] = (seen[s.reviewPeriod] || 0) + 1);
            return {
                period: count > 1 ? `${s.reviewPeriod} (${count})` : s.reviewPeriod,
                overall: Number(s.overallScore),
                ...Object.fromEntries(Object.entries(s.themeAverages || {}).map(([t, v]) => [t, Number(v)])),
            };
        });
    }, [completed]);

    // Optional reviewerUserId overrides the backend default (the vendor's assigned business
    // owner). Returns true/false so the issue modal knows whether to close.
    const issueNow = async (reviewerUserId) => {
        try {
            await apiCall("POST", `/TPRMVendor/${vendorId}/performance-scorecards/issue`,
                reviewerUserId ? { reviewerUserId: Number(reviewerUserId) } : {});
            await fetchData();
            return true;
        } catch (e) {
            setActionError(e?.response?.data?.message || "Could not issue the scorecard.");
            return false;
        }
    };

    const reassign = async (reviewerUserId) => {
        try {
            await apiCall("PUT", `/TPRMVendor/${vendorId}/performance-scorecards/${open.id}`,
                { reviewerUserId: reviewerUserId ? Number(reviewerUserId) : null });
            await fetchData();
        } catch (e) { setActionError(e?.response?.data?.message || "Could not reassign the reviewer."); }
    };

    const setCadence = async (months) => {
        try {
            await apiCall("PUT", `/TPRMVendor/${vendorId}/performance-scorecards/cadence`,
                { frequencyMonths: months ? Number(months) : null });
            await fetchData();
        } catch (e) { setActionError(e?.response?.data?.message || "Could not change the review cadence."); }
    };

    // Returns true/false so callers (e.g. the review modal) know whether to close on success.
    const submit = async () => {
        if (!allAnswered) return false;
        setSaving(true);
        try {
            await apiCall("PUT", `/TPRMVendor/${vendorId}/performance-scorecards/${open.id}/submit`, {
                responses: catalog.questions.map(q => ({ questionId: q.questionId, score: answers[q.questionId] })),
                comments: comments || null,
            });
            setAnswers({}); setComments("");
            await fetchData();
            return true;
        } catch (e) {
            setActionError(e?.response?.data?.message || "Could not submit the scorecard.");
            return false;
        } finally { setSaving(false); }
    };

    return {
        catalog, data, users, loading, error,
        actionError, setActionError,
        answers, setAnswers, comments, setComments, saving,
        scorecards, open, completed, latest, isReviewer, canFill,
        themes, scale, liveThemeAvg, answeredCount, allAnswered, chartData,
        issueNow, reassign, setCadence, submit,
    };
}
